import { useState, useEffect, useCallback } from "react";
import { useSocket } from "../context/SocketContext";
import { todayEspont, TopEspont, getBanner } from "../components/espont";
import siteChange from "../components/siteChange";
import ActiveSite from "../components/activesite";
import ActiveGw from "./getActiveGw";
import { getActiveNyeremenyjatek } from "../components/nyeremenyjatek";
import {
  getActiveContest,
  handleContestBet,
  handleContestStop,
  handleContestWinner,
} from "../components/contest";

export function useOverlayState() {
  const { socket } = useSocket();
  const [hot, setHot] = useState(false);
  const [greenScreen, setGreenScreen] = useState(false);
  const [topEspont, setTopEspont] = useState("");
  const [banner, setBanner] = useState("");
  const [rareOpenData, setRareOpenData] = useState(null);
  const [midbar, setMidbar] = useState(true);
  const [showWheel, setShowWheel] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [currentWinner, setCurrentWinner] = useState(null);
  const [winnerQueue, setWinnerQueue] = useState([]);
  const [participants, setParticipants] = useState([]);
  // twitch_name -> total extra ("boost") tickets, for the GwParticipants "+N" badge
  const [participantBoosts, setParticipantBoosts] = useState({});
  const [keyword, setKeyword] = useState("");
  const [showGw, setShowGw] = useState(false);
  const [removeNyWinner, setRemoveNyWinner] = useState(null);
  const [gwTimer, setGwTimer] = useState(0);
  const [espont, setEspont] = useState({ sima: 0, dupla: 0 });
  const [activeSite, setActiveSite] = useState(null);
  const [nyeremenyJatekData, setNyeremenyJatekData] = useState(null);
  const [activeContest, setActiveContest] = useState(null);
  const [karmaActive, setKarmaActive] = useState(true);
  // Kartyajatek: egyetlen allapot-objektum, mert a fazisok szorosan
  // osszefuggnek (kartyak, szazalekok, nyertes, visszaszamlalo).
  const [cardGame, setCardGame] = useState(null);

  /**
   * Rebuilds the card game from a full server state (REST or socket hydration).
   *
   * The countdown is rebuilt from phaseElapsedMs, not from the server's
   * phaseStartedAt: the overlay counts with its own clock, and on an OBS
   * machine that is a few seconds off the timer would start wrong.
   */
  const applyCardGameState = useCallback((state) => {
    if (!state || state.phase === "idle") {
      setCardGame(null);
      return;
    }
    setCardGame({
      phase: state.phase,
      cards: state.cards ?? [],
      percentages: state.percentages ?? [],
      winnerCardIndex: state.winnerCardIndex ?? null,
      phaseDurationMs: state.phaseDurationMs ?? 0,
      phaseStartedAt: Date.now() - (state.phaseElapsedMs ?? 0),
      winners: state.winners ?? [],
      voteTitle: state.voteTitle ?? "",
      collectKeyword: state.collectKeyword ?? "",
      collectTitle: state.collectTitle ?? "",
      collectSubtitle: state.collectSubtitle ?? "",
      sponsorText: state.sponsorText ?? "",
    });
  }, []);

  const toggleHotWord = useCallback((hotState) => {
    const hotwordElement = document.getElementsByClassName("hot-word")[0];
    const chatElement = document.getElementsByClassName("chat")[0];
    if (hotState) {
      hotwordElement?.classList.remove("hot-word-off");
      chatElement?.classList.add("chat-collapsed");
    } else {
      hotwordElement?.classList.add("hot-word-off");
      chatElement?.classList.remove("chat-collapsed");
    }
    setHot(!hotState);
  }, []);

  const EspontChange = useCallback(async () => {
    const pontok = await todayEspont();
    setEspont({ sima: pontok.sima, dupla: pontok.dupla });
  }, []);

  const startOverlay = useCallback(async () => {
    const site = await ActiveSite();
    if (site) {
      siteChange(site);
      setActiveSite(site);
    }

    await EspontChange();
    setTopEspont((await TopEspont()).username);

    const { users, kw, created_at } = await ActiveGw();
    setGwTimer(created_at);
    if (users) {
      setKeyword(kw);
      setProfiles(users);
      setParticipants(users.map((user) => user.name));
      setParticipantBoosts(
        Object.fromEntries(users.map((user) => [user.name, user.boostTickets ?? 0])),
      );
      toggleHotWord(true);
    }

    const bannerText = await getBanner();
    if (bannerText) {
      setGreenScreen(bannerText.greenscreen);
      setBanner(bannerText.banner);
    }

    const activeData = await getActiveNyeremenyjatek();
    if (activeData) {
      setNyeremenyJatekData(activeData);
    }

    const activeContestData = await getActiveContest();
    if (activeContestData) {
      setActiveContest(activeContestData);
    }

  }, [EspontChange, toggleHotWord]);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    const handleOverlay = async (data) => {
      switch (data.type) {
        case "card_game:start": {
          setCardGame({
            phase: "voting",
            cards: data.data.cards ?? [],
            percentages: (data.data.cards ?? []).map(() => 0),
            winnerCardIndex: null,
            phaseDurationMs: data.data.voteDurationMs ?? 30000,
            phaseStartedAt: Date.now(),
            winners: [],
            voteTitle: data.data.voteTitle ?? "",
            collectKeyword: data.data.collectKeyword ?? "",
            collectTitle: data.data.collectTitle ?? "",
            collectSubtitle: data.data.collectSubtitle ?? "",
            sponsorText: data.data.sponsorText ?? "",
          });
          break;
        }
        case "card_game:vote_update": {
          setCardGame((prev) =>
            prev ? { ...prev, percentages: data.data.percentages ?? prev.percentages } : prev,
          );
          break;
        }
        case "card_game:reveal": {
          setCardGame((prev) =>
            prev
              ? {
                  ...prev,
                  phase: "revealing",
                  percentages: data.data.percentages ?? prev.percentages,
                  winnerCardIndex: data.data.winnerCardIndex ?? null,
                }
              : prev,
          );
          break;
        }
        case "card_game:collect_start": {
          setCardGame((prev) =>
            prev
              ? {
                  ...prev,
                  phase: "collecting",
                  phaseDurationMs: data.data.collectDurationMs ?? 30000,
                  phaseStartedAt: Date.now(),
                }
              : prev,
          );
          break;
        }
        case "card_game:winners": {
          setCardGame((prev) =>
            prev ? { ...prev, phase: "done", winners: data.data.winners ?? [] } : prev,
          );
          break;
        }
        case "card_game:reset": {
          setCardGame(null);
          break;
        }
        case "site": {
          const site = { logo: data.data.logo, desc: data.data.overlay_desc };
          siteChange(site);
          setActiveSite(site);
          break;
        }
        case "banner":
          setGreenScreen(data.updatedOverlay.greenscreen);
          setBanner(data.updatedOverlay.banner);
          break;
        case "espont":
          EspontChange();
          break;
        case "case":
          setRareOpenData(data.data);
          setTimeout(() => setRareOpenData(null), 9000);
          break;
        case "giveaway:create": {
          const { users, kw, created_at } = await ActiveGw();
          setGwTimer(created_at);
          if (users || kw) setKeyword(kw);
          setParticipants([]);
          setParticipantBoosts({});
          setShowGw(true);
          toggleHotWord(true);
          break;
        }
        case "giveaway:join":
          if (data.data) {
            setShowGw(true);
            setParticipants((prev) => [...prev, data.data.user.twitch_name]);
          }
          break;
        case "giveaway:boost":
          if (data.data) {
            setParticipantBoosts((prev) => ({
              ...prev,
              [data.data.user.name]: data.data.totalBoost,
            }));
          }
          break;
        case "giveaway:participant_remove": {
          const { users } = await ActiveGw();
          if (users) {
            setParticipants(users.map((user) => user.name));
            setParticipantBoosts(
              Object.fromEntries(users.map((user) => [user.name, user.boostTickets ?? 0])),
            );
          }
          break;
        }
        case "giveaway:close":
          setShowGw(false);
          toggleHotWord(false);
          setTimeout(() => {
            setKeyword("");
            setParticipants([]);
            setParticipantBoosts({});
            setGwTimer(0);
          }, 2000);
          break;
        case "giveaway:winner": {
          const { users } = await ActiveGw();
          const winner_user = users.find(
            (user) => user.name === data.giveaway.user.twitch_name,
          );
          if (winner_user) {
            setWinnerQueue((prevQueue) => [...prevQueue, winner_user]);
          }
          break;
        }
        case "nyeremenyjatekCreate": {
          const activeData = await getActiveNyeremenyjatek();
          setNyeremenyJatekData(activeData);
          break;
        }
        case "nyeremenyjatekJoin": {
          const joinData = data.data;
          setNyeremenyJatekData((prev) => {
            if (!prev || prev.id !== joinData.nyeremenyjatekId) return prev;

            const previousTickets = Array.isArray(prev.tickets)
              ? prev.tickets
              : [];
            const updatedTickets = [...previousTickets];
            const existingIndex = updatedTickets.findIndex(
              (ticket) => ticket.twitch_id === joinData.twitch_id,
            );

            const newTicketAmount = Number(joinData.tickets ?? 0);

            if (existingIndex >= 0) {
              updatedTickets[existingIndex] = {
                ...updatedTickets[existingIndex],
                tickets:
                  Number(updatedTickets[existingIndex].tickets ?? 0) +
                  newTicketAmount,
                joinedAt: joinData.joinedAt
                  ? new Date(joinData.joinedAt)
                  : updatedTickets[existingIndex].joinedAt,
              };
            } else {
              updatedTickets.push({
                nyeremenyjatekId: joinData.nyeremenyjatekId,
                tickets: newTicketAmount,
                twitch_id: joinData.twitch_id,
                twitch_name: joinData.twitch_name,
                joinedAt: joinData.joinedAt
                  ? new Date(joinData.joinedAt)
                  : new Date(),
              });
            }

            return {
              ...prev,
              tickets: updatedTickets,
              ticketsCount: prev.ticketsCount + newTicketAmount,
            };
          });
          break;
        }
        case "nyeremenyjatekRemove": {
          const removeData = data.data;
          setNyeremenyJatekData((prev) => {
            if (!prev || prev.id !== removeData.giveaway_id) return prev;
            const updatedTickets = prev.tickets.filter(
              (ticket) => ticket.twitch_id !== removeData.twitch_id,
            );
            setRemoveNyWinner(removeData.twitch_id);
            return { ...prev, tickets: updatedTickets };
          });
          break;
        }
        case "nyeremenyjatekWinner": {
          const activeData = await getActiveNyeremenyjatek();
          setNyeremenyJatekData(activeData);
          break;
        }
        case "nyeremenyjatekClose":
          setNyeremenyJatekData(null);
          break;
        case "contest:create":
          setActiveContest(data.data);
          break;
        case "contest:bet":
          setActiveContest((prevContest) =>
            handleContestBet(prevContest, data.data),
          );
          break;
        case "contest:stop":
          setActiveContest((prevContest) => handleContestStop(prevContest));
          break;
        case "contest:winner":
          setActiveContest((prevContest) =>
            handleContestWinner(prevContest, data.data),
          );
          break;
        case "contest:close":
          setActiveContest(null);
          break;
        case "karma:start":
          setKarmaActive(true);
          break;
        default:
          break;
      }

      if (data.message === "hot-on") toggleHotWord(true);
      else if (data.message === "hot-off") toggleHotWord(false);
    };

    socket.on("overlay", handleOverlay);

    return () => {
      socket.off("overlay", handleOverlay);
    };
  }, [socket, EspontChange, toggleHotWord]);

  // Konszolidált hydration — a szerver MINDEN connection/reconnect eseménynél
  // elküldi ezt (server.js io.on("connection", ...)), nem csak az első mountnál.
  // Ez zárja be azt a rést, amit a régi mount-only REST hívások (startOverlay)
  // hagytak: OBS browser source frissítés / hálózati megszakadás után az
  // overlay eddig üresen maradhatott a következő élő eseményig.
  useEffect(() => {
    if (!socket) return;

    const handleHydrate = (data) => {
      // Giveaway — ugyanaz a mapping, mint getActiveGw.jsx-ben
      const activeGw = data.giveaways?.[0];
      if (activeGw?.participants) {
        setGwTimer(activeGw.createdAt);
        setKeyword(activeGw.keyword);
        const users = activeGw.participants.map((p) => ({
          id: p.discord_id,
          name: p.twitch_name,
          avatar: p.image,
          isMod: p.isMod,
          isVip: p.isVip,
        }));
        setProfiles(users);
        setParticipants(users.map((user) => user.name));
        toggleHotWord(true);
      }

      if (data.nyeremenyjatek) {
        setNyeremenyJatekData(data.nyeremenyjatek);
      }

      if (data.contest) {
        setActiveContest(data.contest);
      }

      // Always applied, even when idle: after a reset the hydration has to be
      // able to clear a game left on screen, not only to restore one.
      applyCardGameState(data.cardGame);
    };

    socket.on("overlay:hydrate", handleHydrate);

    return () => {
      socket.off("overlay:hydrate", handleHydrate);
    };
  }, [socket, toggleHotWord, applyCardGameState]);

  // Kartyajatek hydration REST-en, a mountnal. A socket hydration minden
  // connectionnel lefut, ez viszont akkor is behozza a futo kort, ha a socket
  // meg nem all fel (lassu OBS indulas, halozati keses).
  useEffect(() => {
    // Csak futo kort alkalmaz: ha a valasz kesik es kozben megjott egy
    // card_game:start a socketen, egy "idle" valasz letorolne a most indult
    // jatekot. Torlesre ott a socket hydration es a card_game:reset.
    fetch("https://api.palinagyonbuzi.com/api/card-game/overlay")
      .then((res) => res.json())
      .then((state) => {
        if (state && state.phase !== "idle") applyCardGameState(state);
      })
      .catch(() => {});
  }, [applyCardGameState]);

  // Process winner queue
  useEffect(() => {
    const processNextWinner = async () => {
      if (!currentWinner && winnerQueue.length > 0) {
        const nextWinner = winnerQueue[0];

        if (!nextWinner?.name || !nextWinner?.id) {
          setWinnerQueue((prev) => prev.slice(1));
          return;
        }

        try {
          const { users } = await ActiveGw();
          if (users?.length > 0) {
            const validWinner = users.find((u) => u.name === nextWinner.name);
            if (!validWinner)
              throw new Error("Winner not found in active users");

            setProfiles(users);
            setMidbar(false);
            await new Promise((resolve) => setTimeout(resolve, 500));
            setCurrentWinner(validWinner);
            setShowWheel(true);
          } else {
            throw new Error("No users returned from ActiveGw");
          }
        } catch (error) {
          console.error("Error processing winner:", error);
          setWinnerQueue((prev) => prev.slice(1));
        }
      }
    };

    processNextWinner();
  }, [winnerQueue, currentWinner]);

  const handleAnimationComplete = useCallback(() => {
    setShowWheel(false);
    setMidbar(true);
    setTimeout(() => {
      setWinnerQueue((prev) => prev.filter((_, i) => i !== 0));
      setCurrentWinner(null);
    }, 5000);
  }, []);

  return {
    // State
    hot,
    greenScreen,
    topEspont,
    banner,
    rareOpenData,
    midbar,
    showWheel,
    profiles,
    currentWinner,
    participants,
    participantBoosts,
    keyword,
    showGw,
    removeNyWinner,
    gwTimer,
    espont,
    activeSite,
    nyeremenyJatekData,
    activeContest,
    karmaActive,
    cardGame,
    // Actions
    toggleHotWord,
    startOverlay,
    handleAnimationComplete,
    setKarmaActive,
  };
}
